import { isDefined, isNotDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import type { BulkLeg } from '@/store/bulkTrade/state.ts';
import type { AppState } from '@/store/store.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import { clearUndefined } from '@/utils/clearUndefined.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { ObjectWithUndefinedPropertiesNullable } from '@/utils/typescript.ts';

// ██████╗  █████╗ ██████╗  █████╗ ███╗   ███╗███████╗
// ██╔══██╗██╔══██╗██╔══██╗██╔══██╗████╗ ████║██╔════╝
// ██████╔╝███████║██████╔╝███████║██╔████╔██║███████╗
// ██╔═══╝ ██╔══██║██╔══██╗██╔══██║██║╚██╔╝██║╚════██║
// ██║     ██║  ██║██║  ██║██║  ██║██║ ╚═╝ ██║███████║
// ╚═╝     ╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝     ╚═╝╚══════╝

export type TradeCaptureBulkTradeParams = {
  tabId: string;
  bulkTradeId: string;

  currencyPair?: string;
  negotiatedCurrencyIndex?: CurrencyIndex;

  legChanges: Record<string, Partial<ObjectWithUndefinedPropertiesNullable<BulkLeg>>>;
};

//  █████╗ ██████╗ ██╗
// ██╔══██╗██╔══██╗██║
// ███████║██████╔╝██║
// ██╔══██║██╔═══╝ ██║
// ██║  ██║██║     ██║
// ╚═╝  ╚═╝╚═╝     ╚═╝

export const checkBulkTradeByTradeCapture = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<TradeCaptureBulkTradeResponse, TradeCaptureBulkTradeParams>({
    queryFn: async (params, api, _extraOptions, baseQuery) => {
      const state = api.getState() as AppState;
      const body = getCheckBulkTradeBody(params, state);

      const result = await baseQuery({
        url: `/tradeCapture/Bulk/${params.bulkTradeId}`,
        method: 'POST',
        body,
        timeout: 10_000, // 10 seconds
      });

      if (result.error) {
        return { error: result.error };
      }

      const response = result.data as TradeCaptureBulkTradeResponse;
      return { data: response };
    },
  });
};

// ██████╗  ██████╗ ██████╗ ██╗   ██╗
// ██╔══██╗██╔═══██╗██╔══██╗╚██╗ ██╔╝
// ██████╔╝██║   ██║██║  ██║ ╚████╔╝
// ██╔══██╗██║   ██║██║  ██║  ╚██╔╝
// ██████╔╝╚██████╔╝██████╔╝   ██║
// ╚═════╝  ╚═════╝ ╚═════╝    ╚═╝

export type CheckBulkTradeBody = {
  idVersion: number;
  changedFields: {
    currencyPair?: string;
    negotiatedCurrency?: CurrencyIndex;
    legs: Record<string, CheckBulkTradeBodyLeg>;
  };
};

export type CheckBulkTradeBodyLeg = CheckBulkTradeBodyCashLeg | CheckBulkTradeBodySwapLeg;

export type CheckBulkTradeBodyCashLeg = {
  productName: 'FxSpot' | 'FxFwd';

  way?: 'Buy' | 'Sell';

  amount1String?: string | null;
  amount2String?: string | null;

  maturityDateString?: string | null;
};

export type CheckBulkTradeBodySwapLeg = {
  productName: 'FxSwap';

  way?: 'Buy' | 'Sell';
  isUneven?: boolean;

  nearAmount1String?: string | null;
  nearAmount2String?: string | null;

  nearPaymentDateString?: string | null;

  farAmount1String?: string | null;
  farAmount2String?: string | null;

  farPaymentDateString?: string | null;
};

const getCheckBulkTradeBody = (params: TradeCaptureBulkTradeParams, state: AppState): CheckBulkTradeBody => {
  const bulkTrade = selectBulkTrade(state, params.tabId);

  if (isNotDefined(bulkTrade)) {
    throw new Error(`BulkTrader with tabId ${params.tabId} not found`);
  }

  const negotiatedCurrency = params.negotiatedCurrencyIndex ?? bulkTrade.negotiatedCurrencyIndex;

  return {
    idVersion: bulkTrade.tradeCaptureVersion + 1,
    changedFields: {
      currencyPair: params.currencyPair,
      negotiatedCurrency: params.negotiatedCurrencyIndex,
      legs: Object.entries(params.legChanges).reduce(
        (legById, [legId, leg]) => {
          if (leg.type === 'Spot') {
            legById[legId] = {
              productName: 'FxSpot',

              ...clearUndefined({
                way: leg.way,

                amount1String:
                  negotiatedCurrency === 1
                    ? isDefined(leg.notional)
                      ? String(leg.notional)
                      : leg.notional === null
                        ? ''
                        : undefined
                    : undefined,
                amount2String:
                  negotiatedCurrency === 2
                    ? isDefined(leg.notional)
                      ? String(leg.notional)
                      : leg.notional === null
                        ? ''
                        : undefined
                    : undefined,

                maturityDateString: leg.maturityDate,
              }),
            };
          }

          if (leg.type === 'Forward') {
            legById[legId] = {
              productName: 'FxFwd',

              ...clearUndefined({
                way: leg.way,

                amount1String:
                  negotiatedCurrency === 1
                    ? isDefined(leg.notional)
                      ? String(leg.notional)
                      : leg.notional === null
                        ? ''
                        : undefined
                    : undefined,
                amount2String:
                  negotiatedCurrency === 2
                    ? isDefined(leg.notional)
                      ? String(leg.notional)
                      : leg.notional === null
                        ? ''
                        : undefined
                    : undefined,

                maturityDateString: leg.maturityDate,
              }),
            };
          }

          if (leg.type === 'Swap') {
            legById[legId] = {
              productName: 'FxSwap',

              ...clearUndefined({
                way: isDefined(leg.way) ? (leg.way === 'BuyThenSell' ? 'Buy' : 'Sell') : undefined,
                isUneven: leg.isUneven,

                nearAmount1String:
                  negotiatedCurrency === 1
                    ? isDefined(leg.nearNotional)
                      ? String(leg.nearNotional)
                      : leg.nearNotional === null
                        ? ''
                        : undefined
                    : undefined,
                nearAmount2String:
                  negotiatedCurrency === 2
                    ? isDefined(leg.nearNotional)
                      ? String(leg.nearNotional)
                      : leg.nearNotional === null
                        ? ''
                        : undefined
                    : undefined,

                nearPaymentDateString: leg.nearExpiryDate,

                farAmount1String:
                  negotiatedCurrency === 1
                    ? isDefined(leg.farNotional)
                      ? String(leg.farNotional)
                      : leg.farNotional === null
                        ? ''
                        : undefined
                    : undefined,
                farAmount2String:
                  negotiatedCurrency === 2
                    ? isDefined(leg.farNotional)
                      ? String(leg.farNotional)
                      : leg.farNotional === null
                        ? ''
                        : undefined
                    : undefined,

                farPaymentDateString: leg.farExpiryDate,
              }),
            };
          }

          return legById;
        },
        {} as Record<string, CheckBulkTradeBodyLeg>,
      ),
    },
  };
};

// ██████╗ ███████╗███████╗██████╗  ██████╗ ███╗   ██╗███████╗███████╗
// ██╔══██╗██╔════╝██╔════╝██╔══██╗██╔═══██╗████╗  ██║██╔════╝██╔════╝
// ██████╔╝█████╗  ███████╗██████╔╝██║   ██║██╔██╗ ██║███████╗█████╗
// ██╔══██╗██╔══╝  ╚════██║██╔═══╝ ██║   ██║██║╚██╗██║╚════██║██╔══╝
// ██║  ██║███████╗███████║██║     ╚██████╔╝██║ ╚████║███████║███████╗
// ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚══════╝

// TRADE CAPTURE RESPONSE

/**
 * Response from trade capture bulk trade endpoint
 */
export type TradeCaptureBulkTradeResponse = {
  changedFields: TradeCaptureChangedFields;
  errors: TradeCaptureErrors | null;
  warnings: TradeCaptureErrors | null;
  idVersion: number;
};

/**
 *
 */

/**
 * Changed fields from trade capture
 */
export type TradeCaptureChangedFields = {
  currencyPair: string;
  currency1: string;
  currency2: string;
  negotiatedCurrency?: CurrencyIndex;
  legs: Record<string, TradeCaptureLeg>;
  isPriceObsolete: boolean;
  isReadyToPrice: boolean;
  isProductObsolete: boolean;
};

/**
 * Leg data from trade capture
 */
export type TradeCaptureLeg = {
  // required
  productName: 'FxSpot' | 'FxFwd' | 'FxSwap';
  isProductObsolete: boolean;
  isPriceObsolete: boolean;
  isReadyToPrice: boolean;
  way: 'Buy' | 'Sell';

  // --- optional

  negotiatedCurrency?: CurrencyIndex;

  // cash
  amount1?: number | null;
  amount2?: number | null;

  maturityDate?: ISODate;
  maturityDateTenor?: Tenor;

  // swap
  isUneven?: boolean;

  nearAmount1?: number | null;
  nearAmount2?: number | null;

  farAmount1?: number | null;
  farAmount2?: number | null;

  nearPaymentDate?: ISODate;
  nearPaymentDateTenor?: Tenor;

  farPaymentDate?: ISODate;
  farPaymentDateTenor?: Tenor;
};

/**
 * Margin result for a leg
 */
export type MarginResult = {
  salesCredit: unknown[];
  status: string;
  warnings: string[];
  errors: string[];
  infos: string[];
  info: string;
};

/**
 * Errors structure from trade capture
 */
export type TradeCaptureErrors = {
  legs?: Record<string, Record<string, TradeCaptureFieldError[]>>;
};

/**
 * Field error detail from trade capture
 */
export type TradeCaptureFieldError = {
  id: number;
  description: string;
  previousValue: string;
  faultyValue: string;
};
